/**
 * 🟦 NotificationToastListener — Sonner toast en tiempo real para
 * notificaciones de moderación que llegan vía SSE.
 *
 * Diseño:
 *  - Vive a nivel App (montado en `App.js`), NO dentro de la campanita.
 *    Esto garantiza que el toast aparece independientemente de que la
 *    bell esté cerrada, minimizada o no se haya renderizado.
 *  - Reusa el mismo `useNotificationStream` (no duplica EventSource —
 *    el browser deduplica conexiones idénticas; en el peor caso son 2
 *    streams con backpressure tolerante en el broker).
 *  - Solo dispara para `submission_approved` y `submission_rejected`,
 *    los dos casos que el usuario aprobó para esta sesión. Otros tipos
 *    (reward_*, premium_expiring, etc.) siguen apareciendo en la
 *    campanita pero NO disparan toast para no saturar.
 *  - i18n: respeta `damyxia-language` de localStorage (mismo patrón
 *    que ConsentGate — fuera del LanguageContext para sobrevivir a los
 *    re-mounts condicionales de App).
 *  - Click handler: navega a /my-submissions (donde el usuario ve el
 *    detalle y puede reintentar si fue rejected).
 */
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import useNotificationStream from '../../hooks/useNotificationStream';

const TOAST_TYPES = new Set(['submission_approved', 'submission_rejected']);

function readLang() {
  try {
    const v = localStorage.getItem('damyxia-language');
    return v === 'en' ? 'en' : 'es';
  } catch { return 'es'; }
}

export default function NotificationToastListener({ enabled = true }) {
  const navigate = useNavigate();

  useNotificationStream({
    enabled,
    onNotification: (ev) => {
      if (!ev || !TOAST_TYPES.has(ev.type)) return;
      const lang = readLang();
      const title = lang === 'en' ? (ev.title_en || ev.title_es) : (ev.title_es || ev.title_en);
      const desc = lang === 'en' ? (ev.message_en || ev.message_es) : (ev.message_es || ev.message_en);
      const action = {
        label: lang === 'en' ? 'View' : 'Ver',
        onClick: () => navigate('/my-submissions'),
      };
      if (ev.type === 'submission_approved') {
        toast.success(title, { description: desc, action, duration: 8000 });
      } else {
        toast.error(title, { description: desc, action, duration: 10000 });
      }
    },
  });

  return null;
}
